// Memory-aligned buffer allocation for optimal performance

export class AlignedBufferAllocator {
  private static readonly ALIGNMENT = 16; // 16-byte alignment for optimal memory access

  /**
   * Allocate a Float32Array with proper memory alignment
   * @param size Number of float32 elements
   * @returns Aligned Float32Array
   */
  static allocateFloat32(size: number): Float32Array {
    // Calculate bytes needed (4 bytes per float32)
    const bytesNeeded = size * 4;

    // Allocate extra space for alignment
    const buffer = new ArrayBuffer(bytesNeeded + this.ALIGNMENT);

    // Find aligned offset
    const offset = this.getAlignedOffset(buffer);

    // Create view at aligned offset
    return new Float32Array(buffer, offset, size);
  }

  /**
   * Allocate multiple aligned Float32Arrays efficiently
   * @param count Number of arrays
   * @param size Size of each array
   * @returns Array of aligned Float32Arrays
   */
  static allocateFloat32Multiple(count: number, size: number): Float32Array[] {
    const arrays: Float32Array[] = [];

    // Calculate total size with alignment padding
    const bytesPerArray = size * 4;
    const alignedBytesPerArray = Math.ceil(bytesPerArray / this.ALIGNMENT) * this.ALIGNMENT;
    const totalBytes = count * alignedBytesPerArray + this.ALIGNMENT;

    // Allocate one large buffer
    const arrayBuffer = new ArrayBuffer(totalBytes);
    const baseOffset = this.getAlignedOffset(arrayBuffer);

    // Create views for each array
    for (let i = 0; i < count; i++) {
      const offset = baseOffset + i * alignedBytesPerArray;
      arrays.push(new Float32Array(arrayBuffer, offset, size));
    }

    return arrays;
  }

  /**
   * Check if a buffer is properly aligned
   * @param array TypedArray to check
   * @returns true if aligned to 16 bytes
   */
  static isAligned(array: ArrayBufferView): boolean {
    const address = array.byteOffset;
    return address % this.ALIGNMENT === 0;
  }

  /**
   * Get the aligned offset for a buffer
   * @param buffer ArrayBuffer to align
   * @returns Aligned byte offset
   */
  private static getAlignedOffset(buffer: ArrayBuffer): number {
    // In JavaScript, we can't get the actual memory address,
    // but we can ensure alignment within the ArrayBuffer
    const ptr = 0; // Start at beginning
    const misalignment = ptr % this.ALIGNMENT;

    if (misalignment === 0) {
      return 0;
    }

    return this.ALIGNMENT - misalignment;
  }

  /**
   * Create an aligned version of an existing Float32Array
   * @param source Source array to copy
   * @returns New aligned Float32Array with copied data
   */
  static createAlignedCopy(source: Float32Array): Float32Array {
    const aligned = this.allocateFloat32(source.length);
    aligned.set(source);
    return aligned;
  }
}

// Export singleton for convenience
export const alignedAlloc = AlignedBufferAllocator;
